import { useCallback, useMemo, useState } from 'react';
import { PopulatedKlydo } from '../../../api/klydo';
import useWithdrawKlydoMutation from './useWithdrawKlydoMutation';
import useSetPublicationDateMutation from './useSetPublicationDateMutation';
import useCalendarEventQuery from './useCalendarEventQuery';
import useChimePlayer from '../../../components/KlydoPlayer/hooks/useChimePlayer';
import {
  PublicationDestination,
  PublishModalVariant,
  PublishSelection,
} from '../Publication/PublishModal';
import {
  getPublishModalVariant,
  returnsToEditorAfterWithdraw,
} from '../Publication/publicationState';

type UseKlydoReadOnlyViewProps = {
  klydo: PopulatedKlydo;
  onClickEdit: () => void;
};

const useKlydoReadOnlyView = ({
  klydo,
  onClickEdit,
}: UseKlydoReadOnlyViewProps) => {
  const { mutate: withdrawKlydoMutation } = useWithdrawKlydoMutation();
  const { mutate: setPublicationDateMutation } =
    useSetPublicationDateMutation();
  const [publishModalOpen, setPublishModalOpen] = useState<boolean>(false);

  const calendarEventId = klydo?.metadata?.relatedCalendarEventId;
  const { data: calendarEvent } = useCalendarEventQuery(calendarEventId!);

  const chimeUrl = klydo?.chimesUrls?.[0];
  const { isPlaying: isChimePlaying, controller: handleChimePlayPause } =
    useChimePlayer(chimeUrl);

  const strikeUrl = klydo?.strikesUrls?.[0];
  const { isPlaying: isStrikePlaying, controller: handleStrikePlayPause } =
    useChimePlayer(strikeUrl);

  /**
   * Whether this klydo can have its go-live date moved from here.
   *
   * It needs an APPROVED publication request that has not fired yet; that is
   * exactly what the api's `publication-date` endpoint enforces, and it 400s on
   * anything else.
   *
   * Everything else gets no publication action on this page. A sealed klydo used
   * to be able to open a review of its own here — the design removed that button,
   * so submitting is now the editor's job alone.
   */
  const publishModalVariant = useMemo<PublishModalVariant | null>(
    () => getPublishModalVariant(klydo),
    [klydo],
  );

  const handleBackToEdit = useCallback(() => {
    // "Switch to draft" / "Withdraw request": clear the public review axis.
    if (klydo?.public?.reviewStatus) {
      withdrawKlydoMutation({ klydoId: klydo.id });
    }
    // Only a draft then returns to the editor. A collection-sealed klydo stays
    // sealed after withdrawing, and entering the editor anyway opened a DEAD
    // editor whose autosaves silently never persisted — see
    // returnsToEditorAfterWithdraw. It stays in this read-only view, which no
    // longer offers it any publication action.
    if (returnsToEditorAfterWithdraw(klydo)) {
      onClickEdit();
    }
  }, [onClickEdit, klydo, withdrawKlydoMutation]);

  const handlePublish = useCallback(
    (selection: PublishSelection) => {
      // This view never asks for a destination — it only moves the date of a klydo
      // that is already headed for the feed — so the modal always confirms `Feed`.
      if (selection.destination !== PublicationDestination.Feed) return;

      setPublicationDateMutation(
        {
          klydoId: klydo.id,
          publicationTimestamp: selection.publicationTimestamp,
        },
        // Failures surface through useKlydoMutation's error alert, so there is
        // nothing to hand back to the modal — it just stays open.
        { onSuccess: () => setPublishModalOpen(false) },
      );
    },
    [klydo.id, setPublicationDateMutation],
  );

  const handlePublishClick = useCallback(() => {
    // Publication is its own axis: collection membership is managed separately,
    // so go straight to the date picker.
    setPublishModalOpen(true);
  }, []);

  const handlePublishModalClose = useCallback(() => {
    setPublishModalOpen(false);
  }, []);

  return {
    handleBackToEdit,
    handlePublish,
    handlePublishClick,
    publishModalOpen,
    publishModalVariant,
    handlePublishModalClose,
    calendarEvent,
    isChimePlaying,
    handleChimePlayPause,
    isStrikePlaying,
    handleStrikePlayPause,
  };
};

export default useKlydoReadOnlyView;
